import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AccountFillStageEnum,
  EmployeeStatusesEnum,
  PERMISSION_ACTIONS,
  PLATFORM_PERMISSIONS,
} from '@thinknet-gmbh/platform-common';

import { Icon, ICONS, PLATFORM_THEME_COLORS, Popover } from '@thinknet-gmbh/platform-common-ui';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { EMPLOYEE_PAGES } from 'src/pages/employee/employee.constants';
import { useSendSignUpEmailForMember } from 'src/hooks/members/send-sign-up-email-for-member.hook';
import { EMPLOYEE_MODALS } from 'src/pages/employee/sub-pages/employee-list/employee-list.constants';
import { usePermissionCheck } from 'src/hooks/permission-check.hook';
import { useGetCurrentMemberAccount } from 'src/hooks/accounts/get-current-member-account.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useCreateSingleDatevExportFile } from 'src/hooks/payroll-accounting/create-single-datev-file.hook';

import { IEmployeeTableRowProps } from './employee-list-table.interfaces';

const EmployeeTableRowPopover = ({ record, handleModals, limitedView }: IEmployeeTableRowProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['employee', 'auth']);
  const sendSignUpEmailForMember = useSendSignUpEmailForMember(record._id);
  const { member } = useGetCurrentMemberAccount();
  const currentCompanyId = useGetCurrentCompanyId();
  const { checkRule } = usePermissionCheck(PLATFORM_PERMISSIONS.memberManagement);
  const { checkRule: checkPayrollRule } = usePermissionCheck(PLATFORM_PERMISSIONS.payrollAccounting);
  const canUpdate = checkRule(
    PERMISSION_ACTIONS.update,
    accountId => accountId === record.createdBy
  );
  const canExportDatev = checkPayrollRule(PERMISSION_ACTIONS.create);
  const { createSingleDatevExportFile } = useCreateSingleDatevExportFile();

  return (
    <Popover
      elements={[
        {
          label: t('employee:labels.viewDetails'),
          key: ICONS.foodMenu,
          icon: ICONS.foodMenu,
          handler: () =>
            navigate(`${ROUTES.employee}?type=${EMPLOYEE_PAGES.employeeProfile}&id=${record._id}`),
        },
        ...(limitedView
          ? []
          : [
              {
                label: t('employee:labels.createAbsence'),
                key: ICONS.calendarMinus,
                icon: ICONS.calendarMinus,
                ...(!canUpdate && { color: PLATFORM_THEME_COLORS.grayVariations.murky }),
                handler: () => {
                  if (canUpdate) {
                    handleModals(EMPLOYEE_MODALS.createAbsence, record);
                  }
                },
              },
              {
                label: t('employee:employeeInterview.labels.createInterview'),
                icon: ICONS.message,
                key: ICONS.message,
                ...(!canUpdate && { color: PLATFORM_THEME_COLORS.grayVariations.murky }),
                handler: () => {
                  if (canUpdate) {
                    handleModals(EMPLOYEE_MODALS.createInterview, record);
                  }
                },
              },
              ...(canExportDatev
                ? [
                    {
                      label: t('employee:labels.singleDatevExport'),
                      key: 'singleDatevExport',
                      icon: ICONS.upload,
                      handler: () => {
                        createSingleDatevExportFile({
                          variables: {
                            payload: { memberId: record._id, companyId: currentCompanyId },
                          },
                        }).then(result => {
                          const url =
                            result?.data?.createSingleDatevExportFile?.location;
                          if (url) {
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = '';
                            a.click();
                          }
                        });
                      },
                    },
                  ]
                : []),
              {
                label: t('auth:buttons.resendSignUpLink'),
                key: ICONS.send,
                icon: ICONS.send,
                ...(record?.account?.accountFillStage === AccountFillStageEnum.NOT_COMPLETED &&
                canUpdate
                  ? { handler: () => sendSignUpEmailForMember() }
                  : { color: PLATFORM_THEME_COLORS.grayVariations.murky }),
              },
              ...(record?.employeePersonalInfo?.status !== EmployeeStatusesEnum.INACTIVE &&
              member._id !== record._id
                ? [
                    {
                      label: t('employee:buttons.changeStatus'),
                      key: ICONS.xCircleRegular,
                      icon: ICONS.xCircleRegular,
                      color: canUpdate
                        ? PLATFORM_THEME_COLORS.tone.red
                        : PLATFORM_THEME_COLORS.grayVariations.murky,
                      handler: () => {
                        if (canUpdate) {
                          handleModals(EMPLOYEE_MODALS.changeStatus, record);
                        }
                      },
                    },
                  ]
                : []),
            ]),
      ]}
    >
      <Icon
        icon={ICONS.dotsVerticalRounded}
        size={22}
        rightMargin={false}
        color={PLATFORM_THEME_COLORS.primary.grey}
      />
    </Popover>
  );
};

export default EmployeeTableRowPopover;
